import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Save, Camera } from 'lucide-react';
import DefaultSEO from '../components/DefaultSEO';

const EditProfilePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [userPhoto, setUserPhoto] = useState(null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [deleteAvatar, setDeleteAvatar] = useState(false);
    const fileInputRef = React.useRef(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dob: '',
        gender: 'Male'
    });

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }

        // Fetch profile from backend
        const fetchProfile = async () => {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            try {
                const res = await fetch(`${apiBase}/auth/me`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.data) {
                        const u = data.data;
                        const nameParts = (u.name || '').split(' ');
                        setFormData(prev => ({
                            ...prev,
                            firstName: nameParts[0] || '',
                            lastName: nameParts.slice(1).join(' ') || '',
                            email: (u.email && !u.email.includes('@phone.zoyotrip.local')) ? u.email : '',
                            phone: u.phone || '',
                            dob: u.dob ? u.dob.split('T')[0] : prev.dob,
                            gender: u.gender || prev.gender,
                        }));
                        if (u.avatar) {
                            // If it's a relative path, prepend API base URL. If it's absolute, use it.
                            const avatarUrl = u.avatar.startsWith('http') ? u.avatar : `${apiBase.replace('/api/v1', '')}/storage/${u.avatar}`;
                            setUserPhoto(avatarUrl);
                        }
                    }
                } else if (res.status === 401) {
                    localStorage.removeItem('auth_token');
                    navigate('/login', { replace: true });
                }
            } catch (err) {
                console.error('Error fetching profile:', err);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const fullName = `${formData.firstName} ${formData.lastName}`.trim();

            const token = localStorage.getItem('auth_token');
            if (token) {
                const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                
                const formDataToSend = new FormData();
                formDataToSend.append('name', fullName);
                formDataToSend.append('email', formData.email);
                if (formData.dob) formDataToSend.append('dob', formData.dob);
                if (formData.gender) formDataToSend.append('gender', formData.gender);
                if (formData.phone) formDataToSend.append('phone', formData.phone);
                
                if (avatarFile) {
                    formDataToSend.append('avatar', avatarFile);
                } else if (deleteAvatar) {
                    formDataToSend.append('delete_avatar', '1');
                }
                
                await fetch(`${apiBase}/auth/profile`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    },
                    body: formDataToSend
                });
            }

            setLoading(false);
            navigate('/account', { replace: true });
        } catch (error) {
            console.error("Error updating profile:", error);
            setLoading(false);
        }
    };

    return (
        <div className="container py-4 min-vh-100 d-flex flex-column align-items-center justify-content-center" style={{ maxWidth: '600px' }}>
            <DefaultSEO title="Edit Profile | ZoyoTrip" />
            <div className="card shadow-sm border-0">
                <div className="card-body p-4 p-md-5">

                    {/* Profile Picture Section */}
                    <div className="d-flex flex-column align-items-center mb-4">
                        <div className="position-relative">
                            {userPhoto ? (
                                <img src={userPhoto} alt="Profile" className="rounded-circle shadow-sm object-fit-cover" style={{ width: '100px', height: '100px' }} />
                            ) : (
                                <div className="bg-light rounded-circle shadow-sm d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px', backgroundColor: '#9b59b6', color: 'white' }}>
                                    <span className="fs-1 fw-bold">{formData.firstName ? formData.firstName.charAt(0).toUpperCase() : 'Z'}</span>
                                </div>
                            )}
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                className="d-none" 
                                accept="image/*" 
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setAvatarFile(e.target.files[0]);
                                        setUserPhoto(URL.createObjectURL(e.target.files[0]));
                                        setDeleteAvatar(false);
                                    }
                                }}
                            />
                            <button 
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                                className="btn btn-primary rounded-circle position-absolute bottom-0 end-0 p-2 shadow-sm d-flex align-items-center justify-content-center" 
                                style={{ width: '35px', height: '35px' }}
                            >
                                <Camera size={16} />
                            </button>
                        </div>
                        <div className="d-flex align-items-center gap-3 mt-2">
                            <span 
                                className="text-primary small fw-semibold" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                Update Photo
                            </span>
                            {(userPhoto || avatarFile) && (
                                <span 
                                    className="text-danger small fw-semibold" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setUserPhoto(null);
                                        setAvatarFile(null);
                                        setDeleteAvatar(true);
                                    }}
                                >
                                    Delete
                                </span>
                            )}
                        </div>
                    </div>

                    <form onSubmit={handleSave}>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">First Name</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><User size={18} className="text-muted" /></span>
                                    <input type="text" className="form-control" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Last Name</label>
                                <input type="text" className="form-control" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-muted small fw-semibold">Email Address</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0"><Mail size={18} className="text-muted" /></span>
                                <input 
                                    type="email" 
                                    className="form-control border-start-0 ps-0 bg-white" 
                                    name="email" 
                                    value={formData.email} 
                                    onChange={handleInputChange} 
                                    placeholder="Enter your email address"
                                />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-muted small fw-semibold">Phone Number</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><Phone size={18} className="text-muted" /></span>
                                <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Date of Birth</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><Calendar size={18} className="text-muted" /></span>
                                    <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleInputChange} />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Gender</label>
                                <select className="form-select" name="gender" value={formData.gender} onChange={handleInputChange}>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary w-100 py-2 rounded-pill fw-bold d-flex flex-row align-items-center justify-content-center gap-2 mb-5" disabled={loading}>
                            {loading ? 'Saving Profile...' : <><Save size={18} /> Save Profile</>}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditProfilePage;
